import { useState } from "react";
import { Button } from "../components/ui/button";
import { Carnet, Nouveautes } from "./carnet";
import { BoutonLien, GRAND_BOUTON, IconeCamera, NumeroDePage, TitreEcran } from "./commun";
import { Confirmation, DejaFait, Enigme, Refus } from "./ecrans";
import { type Jeu, useJeu, type Vue } from "./jeu";
import { Recommencer, Reprise, SaisieCode, Sauvegarde } from "./outils";
import { finDeLaPartie, type Partie, pageCourante } from "./partie";
import { extraireCode } from "./qr";
import { Scanner } from "./scanner";

/**
 * L'appli compagnon, en une page : la page a lire, le carnet, et les gestes du lecteur
 * (scanner, taper un code, sauvegarder, reprendre, recommencer). Le livre raconte ; le
 * telephone garde la partie et donne la page suivante (spec
 * docs/specs/2026-09-28-livre-jeu-prototype-design.md dans ai-revenue-lab).
 *
 * Le nom du fichier et du composant vient du template, qui recreerait l'accueil d'exemple
 * s'il disparaissait : c'est donc ici que vit l'accueil de l'appli.
 */

type Outil =
  | { type: "scanner" }
  | { type: "saisie" }
  | { type: "sauvegarde"; code: string }
  | { type: "reprise" }
  | { type: "recommencer" };

interface Gestes {
  scanner: () => void;
  taper: () => void;
  sauvegarder: () => void;
  reprendre: () => void;
  recommencer: () => void;
}

export function HomePage() {
  const { vue, jeu } = useJeu();
  const [outil, setOutil] = useState<Outil | null>(null);
  const { livre, partie, attente } = vue;

  const ouvrir = (suivant: Outil) => {
    // Ouvrir un outil range la question en cours : l'ecran ne montre qu'une chose a la fois.
    jeu.fermer();
    setOutil(suivant);
  };
  const fermerOutil = () => setOutil(null);
  const gestes: Gestes = {
    scanner: () => ouvrir({ type: "scanner" }),
    taper: () => ouvrir({ type: "saisie" }),
    sauvegarder: () => {
      const code = jeu.demanderSauvegarde();
      if (code) ouvrir({ type: "sauvegarde", code });
    },
    reprendre: () => ouvrir({ type: "reprise" }),
    recommencer: () => ouvrir({ type: "recommencer" }),
  };

  // La partie qu'effacerait un « Recommencer avec ce choix » confirme.
  const enCours = () =>
    partie ? { page: pageCourante(livre, partie), code: jeu.codeActuel() } : null;

  switch (attente?.type) {
    case "confirmer":
      return <Confirmation attente={attente} enCours={enCours()} jeu={jeu} />;
    case "enigme":
      return <Enigme key={attente.code} attente={attente} enCours={enCours()} jeu={jeu} />;
    case "refus":
      return (
        <Refus
          attente={attente}
          sansPartie={partie === null}
          jeu={jeu}
          onScanner={gestes.scanner}
          onTaper={gestes.taper}
          onRecommencer={gestes.recommencer}
        />
      );
    case "deja-fait":
      return <DejaFait attente={attente} jeu={jeu} />;
    default:
      break;
  }

  switch (outil?.type) {
    case "scanner":
      return (
        <Scanner
          onLu={(contenu) => {
            setOutil(null);
            const code = extraireCode(contenu);
            if (code) jeu.recevoir(code, "scan");
            else jeu.qrEtranger();
          }}
          onTaper={gestes.taper}
          onFermer={fermerOutil}
        />
      );
    case "saisie":
      return (
        <SaisieCode
          onCode={(code) => {
            setOutil(null);
            jeu.recevoir(code, "saisie");
          }}
          onFermer={fermerOutil}
        />
      );
    case "sauvegarde":
      return <Sauvegarde code={outil.code} onFermer={fermerOutil} />;
    case "reprise":
      return (
        <Reprise
          jeu={jeu}
          pageEnCours={partie ? pageCourante(livre, partie) : null}
          onFermer={fermerOutil}
        />
      );
    case "recommencer":
      if (partie) {
        return <Recommencer jeu={jeu} page={pageCourante(livre, partie)} onFermer={fermerOutil} />;
      }
      break;
    default:
      break;
  }

  return partie ? (
    <PageALire vue={vue} partie={partie} jeu={jeu} gestes={gestes} />
  ) : (
    <Bienvenue vue={vue} gestes={gestes} />
  );
}

function TitreDuLivre({ titre }: { titre: string }) {
  return (
    <p className="text-center text-xs uppercase tracking-widest text-muted-foreground">{titre}</p>
  );
}

/** Ce que le lecteur doit savoir de la partie gardee, quand quelque chose cloche. */
function Avis({ vue }: { vue: Vue }) {
  if (vue.indisponible) {
    return (
      <p role="status" className="rounded-lg border border-destructive/40 p-3 text-sm">
        Ce navigateur ne garde pas votre partie (navigation privée ?) : elle disparaîtra avec cet
        onglet. Scannez avec le bouton de l'appli plutôt qu'avec l'appareil photo, et notez souvent
        votre code de sauvegarde.
      </p>
    );
  }
  if (vue.illisible) {
    return (
      <p role="status" className="rounded-lg border border-border p-3 text-sm">
        Votre partie précédente ne correspond plus à ce livre, et l'appli l'a effacée. Reprenez-la
        avec votre code de sauvegarde, ou recommencez à la page 1.
      </p>
    );
  }
  return null;
}

function BoutonsDeChoix({ gestes, principal }: { gestes: Gestes; principal: string }) {
  return (
    <div className="flex flex-col gap-3">
      <Button className={GRAND_BOUTON} onClick={gestes.scanner}>
        <IconeCamera />
        {principal}
      </Button>
      <Button variant="outline" className={GRAND_BOUTON} onClick={gestes.taper}>
        Taper un code
      </Button>
    </div>
  );
}

function Bienvenue({ vue, gestes }: { vue: Vue; gestes: Gestes }) {
  return (
    <section aria-labelledby="titre-bienvenue" className="flex flex-col gap-6">
      <TitreDuLivre titre={vue.livre.titre} />
      <Avis vue={vue} />
      {vue.attente?.type === "recommencee" ? (
        <p role="status" className="text-sm text-muted-foreground">
          Partie effacée de ce téléphone.
        </p>
      ) : null}
      <TitreEcran id="titre-bienvenue" className="font-serif text-3xl font-semibold">
        Ouvrez le livre à la page 1
      </TitreEcran>
      <p>
        Lisez, et quand le livre vous propose un choix, scannez son QR code avec ce téléphone. Il
        retient ce que vous trouvez et vous dit à quelle page aller.
      </p>
      <BoutonsDeChoix gestes={gestes} principal="Scanner un choix" />
      <div>
        <BoutonLien onClick={gestes.reprendre}>
          Reprendre une partie avec un code de sauvegarde
        </BoutonLien>
      </div>
      <p className="text-sm text-muted-foreground">
        Votre partie reste dans ce téléphone : l'appli ne vous demande pas de compte et ne garde
        rien qui vous identifie.
      </p>
    </section>
  );
}

function PageALire({
  vue,
  partie,
  jeu,
  gestes,
}: {
  vue: Vue;
  partie: Partie;
  jeu: Jeu;
  gestes: Gestes;
}) {
  const { livre, attente } = vue;
  const page = pageCourante(livre, partie);
  const fin = finDeLaPartie(livre, partie);
  const code = fin ? jeu.codeActuel() : null;
  return (
    <section aria-labelledby="titre-page" className="flex flex-col gap-6">
      <TitreDuLivre titre={livre.titre} />
      <Avis vue={vue} />
      {attente?.type === "reprise" ? (
        <p role="status" className="text-center text-sm text-muted-foreground">
          Partie reprise.
        </p>
      ) : null}
      <div className="text-center">
        <p className="text-lg">
          {fin ? "C'est la fin du chapitre. Lisez la page" : "Lisez la page"}
        </p>
        {/* La cle refait le titre a chaque page : il reprend le focus, l'annonce suit. */}
        <TitreEcran key={page} id="titre-page" label={`Page ${page}`}>
          <NumeroDePage page={page} />
        </TitreEcran>
        {partie.dernier ? (
          <p className="mt-2 text-muted-foreground">après « {partie.dernier.libelle} »</p>
        ) : null}
      </div>
      {attente?.type === "resultat" ? <Nouveautes changements={attente.changements} /> : null}
      {fin ? (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
          {code ? (
            <p>
              Votre code de sauvegarde :{" "}
              <span className="font-mono font-semibold tracking-wider">{code}</span>. Notez-le sur
              la feuille d'aventure.
            </p>
          ) : null}
          <Button className={GRAND_BOUTON} onClick={gestes.recommencer}>
            Rejouer depuis le début
          </Button>
        </div>
      ) : (
        <BoutonsDeChoix gestes={gestes} principal="Scanner le choix suivant" />
      )}
      <Carnet livre={livre} partie={partie} />
      <nav aria-label="Autres gestes" className="flex flex-wrap gap-x-5 gap-y-2">
        {fin ? null : <BoutonLien onClick={gestes.sauvegarder}>Code de sauvegarde</BoutonLien>}
        <BoutonLien onClick={gestes.reprendre}>Reprendre avec un code</BoutonLien>
        {fin ? null : <BoutonLien onClick={gestes.recommencer}>Recommencer</BoutonLien>}
      </nav>
    </section>
  );
}
